
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getPlanById } from '@/config/pricingPlans';

interface User {
  id: string;
  email: string;
  name?: string;
  isPro: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User | null>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSubscription: () => Promise<boolean>;
}

// Default values
const defaultAuthContext: AuthContextType = {
  user: null,
  loading: true,
  login: async () => null,
  signup: async () => {},
  logout: async () => {},
  checkSubscription: async () => false,
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Supabase
  const fetchUserProfile = async (email: string) => {
    try {
      console.log(`Fetching profile for ${email} from database`);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      console.log(`Profile found for ${email}:`, data);
      return data;
    } catch (error) {
      console.error('Exception fetching profile:', error);
      return null;
    }
  };

  // Get compute credits for a given plan - now makes a database query
  const getComputeCreditsForPlan = async (planId: string): Promise<number> => {
    try {
      // Try to get plan details from the database first
      const { data: planData, error: planError } = await supabase
        .from('plans')
        .select('compute_credits')
        .eq('plan_id', planId)
        .maybeSingle();

      if (planError) {
        console.error('Error fetching plan credits from database:', planError);
      } else if (planData && planData.compute_credits) {
        console.log(`Found plan credits in database: ${planData.compute_credits}`);
        return Number(planData.compute_credits);
      }
      
      // Fall back to config values if database lookup fails
      const planDetails = getPlanById(planId);
      
      // If computeCredits is defined in config, use it
      if (planDetails.computeCredits) {
        console.log(`Using compute credits from config: ${planDetails.computeCredits}`);
        return planDetails.computeCredits;
      }
      
      // Fallback values if compute credits are not defined in the plan
      if (planId === 'professional') {
        return 10000000; // 10 million for Professional plan
      } else if (planId === 'starter') {
        return 2500000; // 2.5 million for Starter plan
      } else {
        return 1000000; // 1 million for Basic plan (default)
      }
    } catch (error) {
      console.error('Error getting compute credits for plan:', error);
      // Use fallback values if there's an error
      if (planId === 'professional') {
        return 10000000;
      } else if (planId === 'starter') {
        return 2500000;
      } else {
        return 1000000;
      }
    }
  };

  // Create a user profile if it doesn't exist
  // This should only be called during normal signup flow - not payment flow
  const createUserProfile = async (email: string, name: string, isSubscribed: boolean = false, planId?: string) => {
    try {
      console.log(`AUTH: Creating/checking profile for ${email} (normal signup flow, not payment)`);
      
      // Check if profile already exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .maybeSingle();
        
      if (fetchError) {
        console.error('Error checking for existing profile:', fetchError);
        return null;
      }
      
      // Calculate compute points based on plan or subscription status
      let computePoints = 1000; // Default compute points for new users
      
      if (planId) {
        // If a specific plan is provided, get credits from that plan
        computePoints = await getComputeCreditsForPlan(planId);
        console.log(`Using compute credits from plan ${planId}: ${computePoints}`);
        isSubscribed = true; // If a plan is specified, user is subscribed
      } else if (isSubscribed) {
        // Default for subscribed users without specific plan
        computePoints = 2500;
      }
      
      // If profile exists, update it
      if (existingProfile) {
        console.log('AUTH: Profile already exists for:', email);
        return existingProfile;
      }
      
      // Create new profile
      console.log(`AUTH: Creating new profile for ${email} with ${computePoints} compute credits (no payment)`);
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          email: email,
          name: name || email.split('@')[0],
          is_subscribed: isSubscribed,
          compute_points: computePoints // Store as number
        })
        .select()
        .single();
        
      if (error) {
        console.error('Error creating profile:', error);
        return null;
      }
      
      console.log('AUTH: Profile created successfully:', data);
      return data;
    } catch (error) {
      console.error('Exception creating profile:', error);
      return null;
    }
  };

  // Login function with Supabase
  const login = async (email: string, password: string): Promise<User | null> => {
    try {
      console.log('Regular user login attempt for:', email);
      
      // For demo purposes, using mock authentication
      // In a real implementation, this would use supabase.auth.signInWithPassword
      const mockUser = {
        id: email.replace('@', '-').replace('.', '-'),
        email,
        name: 'Demo User',
        isPro: false,
      };
      
      // Check if user has a subscription in profiles table
      const profile = await fetchUserProfile(email);
      if (profile) {
        mockUser.name = profile.name || mockUser.name;
        mockUser.isPro = !!profile.is_subscribed;
      } else {
        // Create a profile if it doesn't exist
        const newProfile = await createUserProfile(email, mockUser.name);
        if (newProfile) {
          mockUser.name = newProfile.name;
          mockUser.isPro = !!newProfile.is_subscribed;
        }
      }
      
      console.log('User authenticated:', mockUser);
      setUser(mockUser);
      sessionStorage.setItem('krix_user', JSON.stringify(mockUser));
      toast.success('Login successful');
      return mockUser;
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
      return null;
    }
  };

  const signup = async (email: string, password: string, name?: string) => {
    try {
      // For demo purposes, using mock signup
      const mockUser = {
        id: '123',
        email,
        name: name || 'New User',
        isPro: false,
      };
      
      // Check for order information in session storage
      const orderId = sessionStorage.getItem('last_order_id');
      const purchasedPlan = sessionStorage.getItem('purchased_plan');
      
      console.log('AUTH: Signup with order information:', { 
        orderId, 
        purchasedPlan, 
        email, 
        name 
      });
      
      // Check for existing profile - it should already exist if payment was successful
      const profile = await fetchUserProfile(email);
      
      if (!profile) {
        console.log('AUTH: No existing profile found for paid user, which is unexpected');
        
        // Create a regular profile with default credits
        // This should only happen for normal signups, not payment-based signups
        if (!orderId && !purchasedPlan) {
          console.log('AUTH: Creating regular profile for normal signup (no payment)');
          await createUserProfile(email, mockUser.name);
        } else {
          console.log('AUTH: Payment order found, but no profile exists - this is unexpected');
          console.log('AUTH: Creating minimal profile, edge function will update it when payment is confirmed');
          await createUserProfile(email, mockUser.name, false);
        }
      } else {
        console.log('AUTH: Found existing profile for user:', profile);
        mockUser.name = profile.name || mockUser.name;
        mockUser.isPro = !!profile.is_subscribed;
      }
      
      // Update the payment_orders record with the user_email if needed
      if (orderId) {
        try {
          const { error: orderUpdateError } = await supabase
            .from('payment_orders')
            .update({ user_email: email })
            .eq('order_id', orderId)
            .is('user_email', null);
            
          if (orderUpdateError) {
            console.error('Error updating order with user email:', orderUpdateError);
          }
        } catch (orderUpdateError) {
          console.error('Exception updating order:', orderUpdateError);
        }
      }
      
      setUser(mockUser);
      sessionStorage.setItem('krix_user', JSON.stringify(mockUser));
      
      console.log('User signed up successfully:', mockUser);
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const logout = async () => {
    setUser(null);
    sessionStorage.removeItem('krix_user');
  };

  const checkSubscription = async () => {
    if (!user) return false;
    
    // Check user subscription status in Supabase
    try {
      const profile = await fetchUserProfile(user.email);
      return profile?.is_subscribed || false;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  };

  // Check if user is logged in from session storage
  useEffect(() => {
    const checkSession = async () => {
      try {
        const sessionUser = sessionStorage.getItem('krix_user');
        if (sessionUser) {
          console.log('Found user in session storage');
          const parsedUser = JSON.parse(sessionUser);
          
          // Check for updated subscription status
          if (parsedUser.email) {
            const profile = await fetchUserProfile(parsedUser.email);
            if (profile) {
              parsedUser.isPro = !!profile.is_subscribed;
              sessionStorage.setItem('krix_user', JSON.stringify(parsedUser));
            }
          }
          
          console.log('Setting user from session:', parsedUser);
          setUser(parsedUser);
        } else {
          console.log('No user found in session storage');
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        checkSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
